/** The sandbox the client asked for at sign-up ("Also create a sandbox", backend ESB): created in the background at
 *  Ready, then its booking-system connection, the tour and a few bookings are synced into it. A compact progress
 *  (Requested → Creating → Syncing tour and bookings → Ready, or Failed with the reason in words); the page's one
 *  state poll refreshes it (useOnboardingState polls fast while it is set up). Once ready, "Open sandbox" signs in
 *  to it as the Developer Overview's card does (explicit `developer.sandbox_signin` + an MFA session), and the
 *  mirror of production's webhooks is switched on from Developer → Overview. */
import { Alert, Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DeveloperApiError } from '../developer/api';
import { DEVELOPER_PATHS } from '../developer/routes';
import { useSandboxSignin } from '../developer/SandboxCard';
import { SigninError } from '../developer/SigninError';
import { ContextStore } from '../store/contextStore';
import { LaneStateIcon, Section } from './components';
import { SANDBOX_STATUSES, sandboxErrorWords } from './labels';
import { SANDBOX_STEP_STATE_WORDS, SANDBOX_STEPS, sandboxStepStates, sandboxSyncedWords } from './sandboxProgress';
import type { OnboardingSandbox } from './types';

export const MIRROR_NOTE = "Mirror production's webhooks from Developer → Overview.";

/** The steps as a compact list: icon and words. */
export function SandboxSteps({ sandbox }: { sandbox: OnboardingSandbox }) {
    const states = sandboxStepStates(sandbox);
    return (
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }} data-testid="sandbox-steps">
            {SANDBOX_STEPS.map((step, i) => (
                <Box
                    component="li"
                    key={step}
                    data-testid={`sandbox-step-${step}`}
                    data-state={states[i]}
                    sx={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr)', columnGap: 1, alignItems: 'center', py: 0.25 }}
                >
                    <LaneStateIcon state={states[i]} />
                    <Typography variant="body2" color={states[i] === 'pending' ? 'text.secondary' : 'text.primary'}>
                        {SANDBOX_STATUSES[step]}
                        <Box component="span" sx={visuallyHidden}>{`: ${SANDBOX_STEP_STATE_WORDS[states[i]] || states[i]}`}</Box>
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

function statusWords(sandbox: OnboardingSandbox, ready: boolean): string {
    switch (sandbox.status) {
        case 'requested':
            return ready ? 'Starting to create your sandbox…' : 'We create it once your account is ready.';
        case 'creating':
            return 'Creating your sandbox…';
        case 'syncing': {
            const synced = sandboxSyncedWords(sandbox.steps);
            return `Connecting it to your booking system and copying your tour and a few bookings…${synced ? ` (${synced} so far)` : ''}`;
        }
        case 'ready': {
            const synced = sandboxSyncedWords(sandbox.steps);
            return `Your sandbox is ready${synced ? `, with ${synced}` : ''}. Nothing in it is sent to your guests.`;
        }
        default:
            return SANDBOX_STATUSES[sandbox.status || 'requested'] || String(sandbox.status);
    }
}

const NO_PERMISSION = 'Needs the permission “Developer: sign in to the sandbox” (ask an account admin).';

/** A sandbox without an admin (a console-made tenant's): there is nobody to sign in as; production's card adds one. */
const NoAdmin = () => (
    <Alert severity="info" sx={{ mt: 1.5 }} data-testid="sandbox-no-admin">
        This sandbox has no admin yet: add one in{' '}
        <Link component={RouterLink} to={DEVELOPER_PATHS.overview}>Developer → Overview</Link>
        {' '}(“Add sandbox admin” on the sandbox card).
    </Alert>
);

/** The Onboarding page's sandbox block (only when the state has a sandbox). `phaseReady`: the account is Ready, so a
 *  requested sandbox is about to be created. `navigate`: tests. */
export default function SandboxSetup({ sandbox, phaseReady, navigate }: {
    sandbox: OnboardingSandbox;
    phaseReady: boolean;
    navigate?: (url: string) => void;
}) {
    const { hasExplicitPerm } = React.useContext(ContextStore) as any;
    const signin = useSandboxSignin(navigate);
    const canSignIn = !!hasExplicitPerm?.('developer', 'sandbox_signin');
    const ready = sandbox.status === 'ready';
    const failed = sandbox.status === 'failed';
    // The Developer card's words for this refusal name "Open sandbox as…", which this page does not have.
    const noAdmin = signin.error instanceof DeveloperApiError && signin.error.code === 'no_sandbox_admin';

    return (
        <Section title="Your sandbox" testId="sandbox-setup">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A safe copy of your account for testing: nothing in it is sent to your guests.
            </Typography>
            <SandboxSteps sandbox={sandbox} />
            {failed ? (
                <Alert severity="error" sx={{ mt: 1 }} data-testid="sandbox-setup-error">
                    {sandboxErrorWords(sandbox.error)}
                </Alert>
            ) : (
                <Typography variant="body2" sx={{ mt: 1 }} data-testid="sandbox-setup-status">{statusWords(sandbox, phaseReady)}</Typography>
            )}
            {ready && (
                <Box sx={{ mt: 1.5 }}>
                    <Button
                        size="small"
                        variant="contained"
                        disabled={!canSignIn || signin.busy !== null}
                        onClick={() => signin.open({ landing: 'overview' }, 'open')}
                        startIcon={signin.busy ? <CircularProgress size={14} /> : undefined}
                        aria-describedby={canSignIn ? undefined : 'sandbox-open-reason'}
                    >
                        Open sandbox
                    </Button>
                    {!canSignIn && (
                        <Typography id="sandbox-open-reason" variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} data-testid="sandbox-open-reason">
                            {NO_PERMISSION}
                        </Typography>
                    )}
                    {noAdmin ? <NoAdmin /> : <SigninError error={signin.error} />}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="sandbox-mirror-note">
                        <Link component={RouterLink} to={DEVELOPER_PATHS.overview}>{MIRROR_NOTE}</Link>
                    </Typography>
                </Box>
            )}
        </Section>
    );
}
